import type { ReactNode } from 'react'

import { classifyPoint } from './classifyPoint'
import { useChartSeries } from './ChartSeriesContext'
import { SummaryMode } from './types'
import type { ChartSeries, MetricUnit } from './types'

const baseId = (id: string): string => id.replace(/-comparison$/, '')

export interface ChartTooltipValueEntry {
  id: string
  label: string
  color: string
  icon?: ReactNode
  value: number
  unit?: MetricUnit
  /** How the series is aggregated (Total → drawn as bars, Latest → line). Lets a
   *  tooltip pick a swatch matching the mark (e.g. hatched fill vs dashed). */
  summaryMode?: SummaryMode
}

export type ChartTooltipNoDataEntry = Omit<
  ChartTooltipValueEntry,
  'value' | 'unit'
>

export interface ChartTooltipEntries {
  /** The hovered day (ms). */
  timestampMs: number
  /** Primary-series values at the hovered point. */
  primaryEntries: ChartTooltipValueEntry[]
  /** Primary series whose point is a genuine gap ("No data available"). */
  noDataEntries: ChartTooltipNoDataEntry[]
  /** Comparison-overlay values at the hovered point. */
  comparisonEntries: ChartTooltipValueEntry[]
  /** A representative comparison original date, for the comparison group header. */
  comparisonOriginalDate?: string
}

/**
 * Derives a tooltip's entries from the series registry at the hovered timestamp —
 * the shared, context-driven source of truth (colour, label, value, and the
 * value / no-data / comparison classification) that every chart's tooltip must
 * present consistently. The *layout and date formatting* are left to each chart,
 * so different graphs can render different tooltip layouts from the same data.
 * {@link ChartTooltipView} is the default view built on this hook.
 *
 * Returns null when the tooltip is inactive or there is nothing to show.
 */
export function useChartTooltipEntries(
  active: boolean | undefined,
  label: string | number | undefined,
  { showIcons = false }: { showIcons?: boolean } = {},
): ChartTooltipEntries | null {
  const series = useChartSeries()

  if (!active || label == null) return null

  const timestampMs = Number(label)
  const primaryEntries: ChartTooltipValueEntry[] = []
  const noDataEntries: ChartTooltipNoDataEntry[] = []
  const comparisonEntries: ChartTooltipValueEntry[] = []
  let comparisonOriginalDate: string | undefined

  const findPrimary = (id: string): ChartSeries | undefined =>
    series.find((s) => s.id === baseId(id))

  for (const s of series) {
    if (s.kind === 'comparison') {
      // Per-day (Total) comparisons resolve to the exact bucket. A cumulative
      // (Latest) comparison carries the last observed value forward across empty
      // buckets — the level persists — otherwise a wide/bucketed range (where a
      // bucket has no observation) drops it from the tooltip entirely and the
      // overlay looks broken.
      const isTotalSeries = s.summaryMode === SummaryMode.TOTAL
      let point = classifyPoint(s, timestampMs)
      if (point.kind === 'noData' && !isTotalSeries) {
        point = classifyPoint(s, timestampMs, { carryForward: true })
      }
      if (point.kind === 'noData') continue
      const primary = findPrimary(s.id)
      comparisonEntries.push({
        id: s.id,
        label: primary?.label ?? s.label,
        color: s.color,
        icon: showIcons ? (primary?.icon ?? s.icon) : undefined,
        value: point.value,
        unit: primary?.unit ?? s.unit,
        summaryMode: s.summaryMode,
      })
      comparisonOriginalDate ??= point.originalDate
      continue
    }

    const point = classifyPoint(s, timestampMs)
    if (point.kind === 'noData') {
      // A gap in a per-day Total (Posts) is just "no bar", not missing data.
      if (s.summaryMode === SummaryMode.TOTAL) continue
      noDataEntries.push({
        id: s.id,
        label: s.label,
        color: s.color,
        icon: showIcons ? s.icon : undefined,
      })
      continue
    }

    primaryEntries.push({
      id: s.id,
      label: s.label,
      color: s.color,
      icon: showIcons ? s.icon : undefined,
      value: point.value,
      unit: s.unit,
    })
  }

  if (
    primaryEntries.length === 0 &&
    noDataEntries.length === 0 &&
    comparisonEntries.length === 0
  ) {
    return null
  }

  return {
    timestampMs,
    primaryEntries,
    noDataEntries,
    comparisonEntries,
    comparisonOriginalDate,
  }
}
